import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { useAuth } from "@/contexts/AuthContext"
import { useNavigate } from "react-router-dom"
import { Clock } from "lucide-react"

export function RegisterForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const [trialInfo, setTrialInfo] = useState<any>(null)

  const { register } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError("")
    setSuccessMessage("")

    try {
      // Get reCAPTCHA token
      let recaptchaToken = ''
      try {
        // @ts-ignore - grecaptcha is loaded from external script
        if (window.grecaptcha) {
          // @ts-ignore
          recaptchaToken = await window.grecaptcha.execute('6LcY7PArAAAAAGvUYeUlZ0AcrlNl0l5d8y1dswQY', { action: 'register' })
        }
      } catch (recaptchaError) {
        console.error('reCAPTCHA error:', recaptchaError)
        // Continue without token - backend will handle validation
      }

      const username = email.split('@')[0] // Generate username from email
      const full_name = `${firstName} ${lastName}`.trim()

      const result = await register({
        email,
        username,
        password,
        full_name: full_name || undefined,
        recaptcha_token: recaptchaToken
      })

      if (result.success) {
        setSuccessMessage(result.message || "Registration successful!")
        setTrialInfo(result.trial_info)

        // Redirect after showing success message
        setTimeout(() => {
          navigate("/dashboard")
        }, 2000)
      } else {
        setError(result.error || "Registration failed. Please try again.")
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle>Create an account</CardTitle>
          <CardDescription>
            Enter your information to create an account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-6">
              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
                  {error}
                </div>
              )}
              {successMessage && (
                <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        {successMessage}
                      </p>
                      {trialInfo && (
                        <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                          <p><strong>{trialInfo.duration_days} days</strong> of full access</p>
                          <p><strong>{trialInfo.scans_per_day} scans per day</strong></p>
                          <p className="mt-1 text-xs">{trialInfo.features}</p>
                        </div>
                      )}
                      <p className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                        Redirecting to dashboard...
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-3">
                  <Label htmlFor="first-name">First name</Label>
                  <Input
                    id="first-name"
                    placeholder="Max"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoComplete="given-name"
                    required
                  />
                </div>
                <div className="grid gap-3">
                  <Label htmlFor="last-name">Last name</Label>
                  <Input
                    id="last-name"
                    placeholder="Robinson"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    autoComplete="family-name"
                    required
                  />
                </div>
              </div>
              <div className="grid gap-3">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="m@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>
              <div className="grid gap-3">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>
              <div className="flex flex-col gap-3">
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? "Creating account..." : "Create account"}
                </Button>
                <Button variant="outline" className="w-full" disabled>
                  Sign up with Google (Coming Soon)
                </Button>
              </div>
            </div>
            <div className="mt-4 text-center text-sm">
              Already have an account?{" "}
              <a href="/login" className="underline underline-offset-4">
                Sign in
              </a>
            </div>
            <div className="mt-2 text-center text-xs text-muted-foreground">
              <a href="/privacy" className="hover:underline">Privacy Policy</a>
              {" • "}
              <a href="/terms" className="hover:underline">Terms of Service</a>
            </div>
            <div className="mt-2 text-center text-xs text-muted-foreground">
              This site is protected by reCAPTCHA and the Google{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="hover:underline">Privacy Policy</a>
              {" and "}
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="hover:underline">Terms of Service</a>
              {" apply."}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}